import * as tf from '@tensorflow/tfjs';

interface DetectionResult {
  found: boolean;
  guidance: string;
  position?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export const detectStrip = async (tensor: tf.Tensor3D): Promise<DetectionResult> => {
  try {
    if (!tensor) {
      console.error('Strip detection: Tensor is null or undefined');
      return {
        found: false,
        guidance: "Invalid image data received"
      };
    }

    const width = tensor.shape[1];
    const height = tensor.shape[0];
    
    const silhouette = {
      x: Math.floor(width * 0.44),   
      y: Math.floor(height * 0.05),   
      width: Math.floor(width * 0.12), 
      height: Math.floor(height * 0.75) 
    };

    let grayscale;
    try {
      grayscale = tf.mean(tensor, -1);
      const data = await grayscale.data();

      const regions = {
        top: {
          x: Math.floor(silhouette.x + silhouette.width * 0.5),
          y: Math.floor(silhouette.y + silhouette.height * 0.1),
          size: 20
        },
        upperMiddle: {
          x: Math.floor(silhouette.x + silhouette.width * 0.5),
          y: Math.floor(silhouette.y + silhouette.height * 0.3),
          size: 20
        },
        center: {
          x: Math.floor(silhouette.x + silhouette.width * 0.5),
          y: Math.floor(silhouette.y + silhouette.height * 0.5),
          size: 20
        },
        lowerMiddle: {
          x: Math.floor(silhouette.x + silhouette.width * 0.5),
          y: Math.floor(silhouette.y + silhouette.height * 0.7),
          size: 20
        },
        bottom: {
          x: Math.floor(silhouette.x + silhouette.width * 0.5),
          y: Math.floor(silhouette.y + silhouette.height * 0.9),
          size: 20
        }
      };

      const analyzeRegion = (region: { x: number, y: number, size: number }) => {
        let sum = 0;
        let count = 0;
        let darkPixels = 0;
        let brightPixels = 0;
        let whiteSurface = 0;
        const halfSize = Math.floor(region.size / 2);

        for (let y = region.y - halfSize; y < region.y + halfSize; y++) {
          for (let x = region.x - halfSize; x < region.x + halfSize; x++) {
            if (x >= 0 && x < width && y >= 0 && y < height) {
              const value = data[y * width + x];
              sum += value;
              count++;
              if (value < 100) darkPixels++;
              if (value > 160) brightPixels++;
              if (value > 200) whiteSurface++;
            }
          }
        }

        const brightRatio = count > 0 ? brightPixels / count : 0;
        const whiteSurfaceRatio = count > 0 ? whiteSurface / count : 0;

        return {
          brightness: count > 0 ? sum / count : 0,
          darkRatio: count > 0 ? darkPixels / count : 0,
          brightRatio,
          whiteSurfaceRatio,
          hasContent: brightRatio > 0.5 || whiteSurfaceRatio > 0.3
        };
      };

      const leftRegion = analyzeRegion({
        x: Math.floor(silhouette.x - silhouette.width * 0.5),
        y: Math.floor(silhouette.y + silhouette.height * 0.5),
        size: 20
      });

      const centerRegion = analyzeRegion({
        x: Math.floor(silhouette.x + silhouette.width * 0.5),
        y: Math.floor(silhouette.y + silhouette.height * 0.5),
        size: 20
      });

      const rightRegion = analyzeRegion({
        x: Math.floor(silhouette.x + silhouette.width * 1.5),
        y: Math.floor(silhouette.y + silhouette.height * 0.5),
        size: 20
      });

      const hasLeftContent = leftRegion.hasContent;
      const hasCenterContent = centerRegion.hasContent;
      const hasRightContent = rightRegion.hasContent;

      if (!hasCenterContent) {
        if (hasLeftContent) {
          return {
            found: false,
            guidance: "Move the strip to the right"
          };
        }
        if (hasRightContent) {
          return {
            found: false,
            guidance: "Move the strip to the left"
          };
        }
        return {
          found: false,
          guidance: "Position the test strip within the frame"
        };
      }

      const topRegion = analyzeRegion({
        x: Math.floor(silhouette.x + silhouette.width * 0.5),
        y: Math.floor(silhouette.y + silhouette.height * 0.2),
        size: 20
      });

      const bottomRegion = analyzeRegion({
        x: Math.floor(silhouette.x + silhouette.width * 0.5),
        y: Math.floor(silhouette.y + silhouette.height * 0.8),
        size: 20
      });

      const isVerticallyAligned = topRegion.hasContent && bottomRegion.hasContent;

      if (!isVerticallyAligned) {
        if (!topRegion.hasContent) {
          return {
            found: false,
            guidance: "Move the strip up slightly"
          };
        }
        if (!bottomRegion.hasContent) {
          return {
            found: false,
            guidance: "Move the strip down slightly"
          };
        }
      }

      const avgBrightness = centerRegion.brightness;
      if (avgBrightness < 30) {
        return {
          found: false,
          guidance: "Move the strip closer to the camera"
        };
      }
      if (avgBrightness > 240) {
        return {
          found: false,
          guidance: "Move the strip further from the camera"
        };
      }

      if (hasCenterContent && isVerticallyAligned) {
        return {
          found: true,
          guidance: "Perfect! Strip is aligned correctly",
          position: silhouette
        };
      }

      return {
        found: false,
        guidance: "Adjust the strip position to fit within the frame"
      };

    } catch (e) {
      console.error('Error in strip detection:', e);
      return {
        found: false,
        guidance: "Please try again"
      };
    } finally {
      if (grayscale && !grayscale.isDisposed) {
        grayscale.dispose();
      }
    }
  } catch (error) {
    console.error('Strip detection error:', error);
    return {
      found: false,
      guidance: "Please try again"
    };
  } finally {
    if (tensor && !tensor.isDisposed) {
      try {
        tensor.dispose();
      } catch (e) {
        console.error('Error disposing tensor:', e);
      }
    }
  }
}; 